import { z } from 'zod'
import { AppError } from '@/shared/domain/AppError'
import { leadLostReasons, type LeadContact, type LeadRecord, type LeadStatus } from '../../domain/lead'

export const legacyLeadRowSchema = z.object({
  id: z.string(),
  numero: z.string().nullable(),
  prenom: z.string().nullable(),
  nom: z.string().nullable(),
  telephone: z.string().nullable(),
  courriel: z.string().nullable(),
  adresse: z.string().nullable(),
  ville: z.string().nullable(),
  code_postal: z.string().nullable(),
  latitude: z.coerce.number().nullable().optional(),
  longitude: z.coerce.number().nullable().optional(),
  type_service: z.string().nullable(),
  message: z.string().nullable(),
  source: z.string().nullable(),
  statut: z.enum(['nouveau', 'contacte', 'soumission_envoyee', 'converti', 'perdu']),
  statut_v3: z
    .enum(['NEW', 'CONTACTED', 'QUALIFIED', 'QUOTE_PREPARED', 'QUOTE_SENT', 'WON', 'LOST', 'ARCHIVED'])
    .nullable()
    .optional(),
  version: z.number().int().positive().nullable().optional(),
  statut_v4: z
    .enum([
      'NEW',
      'TO_CONTACT',
      'CALLBACK',
      'NO_ANSWER',
      'CONTACTED_NOT_INTERESTED',
      'INTERESTED',
      'CONVERTED',
      'ARCHIVED',
    ])
    .nullable()
    .optional(),
  lost_reason: z.enum(leadLostReasons).nullable().optional(),
  lost_note: z.string().nullable().optional(),
  converted_client: z
    .array(z.object({ id: z.uuid(), numero: z.string().nullable() }))
    .nullable()
    .optional(),
  reminder: z
    .array(
      z.object({
        id: z.uuid(),
        kind: z.enum(['FIRST_CONTACT', 'ESCALATION', 'DAILY', 'MANUAL']),
        level: z.number().int().positive(),
        due_at: z.string(),
        message: z.string().nullable(),
        status: z.enum(['PENDING', 'COMPLETED', 'CANCELLED']),
      }),
    )
    .nullable()
    .optional(),
  assigne_a: z.string().nullable(),
  rappel_le: z.string().nullable(),
  rappel_note: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
})

export type LegacyLeadRow = z.infer<typeof legacyLeadRowSchema>

/**
 * Correspondance gelée du registre `docs/migration/status-mappings.md`. Aucun
 * statut n'est déduit par intuition : la section 22 l'interdit explicitement.
 */
const statusMap: Record<LegacyLeadRow['statut'], LeadStatus> = {
  nouveau: 'NEW',
  contacte: 'CONTACTED',
  soumission_envoyee: 'QUOTE_SENT',
  converti: 'WON',
  perdu: 'LOST',
}

function optionalText(value: string | null | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized === '' ? undefined : normalized
}

function mapContact(row: LegacyLeadRow): LeadContact {
  const displayName =
    [optionalText(row.prenom), optionalText(row.nom)].filter(Boolean).join(' ') || 'Lead sans nom'
  const phone = optionalText(row.telephone)
  const email = optionalText(row.courriel)?.toLocaleLowerCase('fr-CA')
  const line1 = optionalText(row.adresse)
  const city = optionalText(row.ville)
  const postalCode = optionalText(row.code_postal)
  return {
    displayName,
    ...(phone ? { phone } : {}),
    ...(email ? { email } : {}),
    ...(line1
      ? { address: { line1, ...(city ? { city } : {}), ...(postalCode ? { postalCode } : {}) } }
      : {}),
    ...(row.latitude !== null &&
    row.latitude !== undefined &&
    row.longitude !== null &&
    row.longitude !== undefined
      ? { latitude: row.latitude, longitude: row.longitude }
      : {}),
  }
}

export function mapLegacyLead(row: LegacyLeadRow): LeadRecord {
  if (!row.created_at || !row.updated_at) {
    throw new AppError('UNEXPECTED_ERROR', 'Un lead legacy ne possède pas ses horodatages.', {
      leadId: row.id,
    })
  }
  const number = optionalText(row.numero)
  const serviceType = optionalText(row.type_service)
  const message = optionalText(row.message)
  const origin = optionalText(row.source)
  const followUpAt = optionalText(row.rappel_le)
  const followUpNote = optionalText(row.rappel_note)
  const pendingReminder = row.reminder?.find((reminder) => reminder.status === 'PENDING')
  return {
    id: row.id,
    ...(number ? { number } : {}),
    // `statut_v3` fait autorité dès qu'il existe : il porte les statuts sans
    // équivalent legacy. Sinon, la correspondance gelée du registre s'applique.
    // Un lead soft-supprimé s'affiche `ARCHIVED`, même si aucune requête ne
    // devrait plus jamais le lui montrer (RLS le filtre déjà en amont).
    status: row.deleted_at ? 'ARCHIVED' : (row.statut_v3 ?? statusMap[row.statut]),
    ...(row.statut_v4 ? { statusV4: row.statut_v4 } : {}),
    version: row.version ?? 1,
    ...(row.lost_reason ? { lostReason: row.lost_reason } : {}),
    ...(row.lost_note ? { lostNote: row.lost_note } : {}),
    ...(row.converted_client?.[0]
      ? {
          convertedClientId: row.converted_client[0].id,
          ...(row.converted_client[0].numero
            ? { convertedClientNumber: row.converted_client[0].numero }
            : {}),
        }
      : {}),
    contact: mapContact(row),
    ...(serviceType ? { serviceType } : {}),
    ...(message ? { message } : {}),
    ...(origin ? { origin } : {}),
    ...(row.assigne_a ? { assignedEmployeeId: row.assigne_a } : {}),
    ...(followUpAt ? { followUpAt } : {}),
    ...(followUpNote ? { followUpNote } : {}),
    // Seule la prochaine action ouverte compte : l'index unique garantit qu'il
    // n'y en a jamais deux, les autres lignes appartiennent à l'historique.
    ...(pendingReminder
      ? {
          reminder: {
            id: pendingReminder.id,
            kind: pendingReminder.kind,
            level: pendingReminder.level,
            dueAt: pendingReminder.due_at,
            ...(pendingReminder.message ? { message: pendingReminder.message } : {}),
          },
        }
      : {}),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}
